import { memo, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { HStack, Heading, VStack } from '@library';
import SpinnerPage from '../../../../core/components/pages/SpinnerPage';
import useTabletView from '../../../../core/hooks/dom/useTabletView';
import FilterSideBar from '../Filters/FilterSideBar';
import ProductListHeader from '../Header/ProductListHeader';
import ProductListCategoryHeader from '../Header/ProductListCategoryHeader';
import type { ProductWithRelatedData, ProductsViewMode } from '../../../types';
import FadeAnimation from '../../../../core/components/animations/FadeAnimation';
import ListPagination from '../Controls/ListPagination';
import useImmutableCallback from '../../../../core/hooks/useImmutableCallback';
import useAvailableViewModes from '../../../hooks/useAvailableViewModes';
import ProductAppliedFiltersList from '../Filters/ProductAppliedFiltersList';
import Container from '../../../../core/components/Container';
import ProductListMainContent from './ProductListMainContent';
import styles from './ProductListBody.module.css';

const useViewMode = () => {
    const [params, setSearchParams] = useSearchParams();

    const viewModeFromParams = params.get('viewMode') as
        | ProductsViewMode
        | undefined;

    const [viewMode, setViewMode] = useState<ProductsViewMode | undefined>(
        undefined
    );

    const viewModes = useAvailableViewModes();

    const changeViewMode = useImmutableCallback((value: ProductsViewMode) => {
        setViewMode(value);
        params.set('viewMode', value);
        setSearchParams(params, { preventScrollReset: true });
    });

    const validatedViewMode = useMemo(() => {
        const selectedViewMode = viewModeFromParams ?? viewMode;
        if (selectedViewMode && viewModes.includes(selectedViewMode)) {
            return selectedViewMode;
        } else {
            return viewModes[0];
        }
    }, [viewMode, viewModes, viewModeFromParams]);

    return { viewMode: validatedViewMode, changeViewMode };
};

const ProductListBody = ({
    sidebar,
    header,
    subHeader,
    items,
    loading = false,
    total,
    filteredTotal,
    totalLoading = false,
    pageSize,
    ignoreList,
    banner,
    headerAction,
}: {
    sidebar: ReactNode;
    header?: ReactNode;
    banner?: ReactNode;
    subHeader?: ReactNode;
    headerAction?: ReactNode;
    items: ProductWithRelatedData[];
    loading?: boolean;
    total: number;
    filteredTotal: number;
    totalLoading?: boolean;
    pageSize: number;
    ignoreList?: string[];
}) => {
    const [showFilters, setShowFilters] = useState(false);
    const isTabletView = useTabletView();
    const gap = isTabletView ? 'medium' : 'small';

    const { viewMode, changeViewMode } = useViewMode();

    const isNotReady = loading;
    return (
        <VStack>
            <div className={styles.header}>
                <Container>
                    <ProductListHeader
                        header={header}
                        subHeader={subHeader}
                        headerAction={headerAction}
                        totalCount={total}
                        filteredTotal={filteredTotal}
                        totalLoading={totalLoading}
                        viewMode={viewMode}
                        setViewMode={changeViewMode}
                        openFilters={() => {
                            setShowFilters(true);
                        }}
                    />
                </Container>
            </div>

            <Container>
                <HStack justify="spaceBetween">
                    <FilterSideBar
                        header={
                            <ProductListCategoryHeader
                                header={header}
                                subHeader={subHeader}
                                total={total}
                                filteredTotal={filteredTotal}
                                totalLoading={totalLoading}
                            />
                        }
                        ignoreList={ignoreList}
                        show={showFilters}
                        onClose={() => setShowFilters(false)}
                    >
                        {sidebar}
                    </FilterSideBar>
                    <div className={styles.searchResultsProducts}>
                        {banner}
                        <div className={styles.searchResultsProducts}>
                            {items.length > 0 ? (
                                <VStack gap={gap}>
                                    <ProductAppliedFiltersList
                                        ignoreList={ignoreList}
                                    />
                                    <ProductListMainContent
                                        viewMode={viewMode}
                                        products={items}
                                    />
                                    <HStack justify="end">
                                        <ListPagination
                                            filteredTotal={filteredTotal}
                                            pageSize={pageSize}
                                        />
                                    </HStack>
                                </VStack>
                            ) : (
                                <>
                                    {isNotReady ? (
                                        <SpinnerPage />
                                    ) : (
                                        <VStack gap={gap}>
                                            <ProductAppliedFiltersList
                                                ignoreList={ignoreList}
                                            />
                                            <div
                                                className={
                                                    styles.emptyListMessage
                                                }
                                                data-test-class="productListEmptyMessage"
                                            >
                                                <Heading h3 center>
                                                    <FormattedMessage id="sorry, no products matching your search could be found." />
                                                </Heading>
                                            </div>
                                        </VStack>
                                    )}
                                </>
                            )}
                            <FadeAnimation show={loading && items.length > 0}>
                                <div
                                    data-test-class="loading-overlay"
                                    className={styles.productListLoadingOverlay}
                                />
                            </FadeAnimation>
                        </div>
                    </div>
                </HStack>
            </Container>
        </VStack>
    );
};

export default memo(ProductListBody);
